import type { PlanNode } from '@timelog/pm-shared';

import type { EditField } from '../editor';

/**
 * Which of a row's cells are editable, and in what order Tab walks them.
 *
 * One predicate, three consumers — `PlanRow` and `TimelineRow` (does the Hours
 * cell render an input?) and `useFieldEditing` (does Tab stop there?). They
 * used to each carry their own copy, which drifted: the row asked
 * `kind !== 'deliverable'` and the Tab cycle asked `kind === 'task'`, so on a
 * top-level row the cell was read-only *and* silently skipped between the name
 * and the target date.
 *
 * Exactly one thing takes the hours away from a row: **children**, which
 * aggregate them (BR-1/CALC-3). Allocations no longer do — they are an
 * independent quantity the budget is compared against (BR-28/ADR 0030), and a
 * staffed task must still be able to state what it is worth.
 *
 * `kind` deliberately plays no part. A root row is stored as `deliverable`
 * (BR-2) but the UI has no Deliverable concept, and a childless root row owns
 * its hours — the case ADR 0028 opened up in the document schema.
 */
export const isHoursEditable = (node: PlanNode): boolean => node.children.length === 0;

/** The Tab cycle: name → hours → start → target date, minus what this row can't hold. */
export const editFieldOrder = (node: PlanNode): EditField[] =>
  (['title', 'hours', 'start', 'target'] as const).filter(
    (field) => field !== 'hours' || isHoursEditable(node),
  );

/**
 * Whether a row may hold allocations — the gate behind every resource
 * affordance: the list kebab's "Add resources", its summary line, and the
 * timeline's allocation stack.
 *
 * Children are the only thing that takes staffing away, exactly as they are the
 * only thing that takes the hours away. `kind` used to play a part: BR-2
 * refused allocations on a root row, so a plan of standalone top-level tasks
 * had no way to staff any of them and the kebab offered nothing. ADR 0036
 * retired that clause; BR-1 still refuses allocations on a parent.
 *
 * It is a separate predicate from `isHoursEditable` despite reading the same
 * today: they answer different questions and have already diverged once.
 */
export const areResourcesEditable = (node: PlanNode): boolean => node.children.length === 0;
